import { SelectionModel } from '@angular/cdk/collections';
import { FlatTreeControl } from '@angular/cdk/tree';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { ExtraButtonEvent, ExtraButtons, TreeSelectionChangeEvent } from 'app/models';
import { GenericTreeType, Node } from './generic-tree-type';
import { StatusElementaPorukeEnum, StatusSvojstvaEnum } from 'app/models/shared/enums';

@Component({
  selector: 'app-generic-tree',
  templateUrl: './generic-tree.component.html',
  styleUrls: ['./generic-tree.component.scss']
})
export class GenericTreeComponent implements OnInit, OnChanges {
  @Input() dugmiciZaAkcije: ExtraButtons[] = [];
  @Input() podaci: GenericTreeType[] = [];
  @Input() sifreZaSelectCheckboxa: string[] = [];
  @Input() disabledCheckbox: boolean = false;
  @Input() cb: boolean = false;
  @Input() singleCb: boolean = false;
  @Input() expandAll: boolean = false;
  @Input() expandFirstLevel: boolean = false;
  @Input() prekoPutanje: boolean = false;
  @Input() sacuvajExpandState: boolean = false;

  @Output() dugmiciZaAkcijeClick = new EventEmitter<ExtraButtonEvent>();
  @Output() todoLeafItemSelectionToggleClick = new EventEmitter<TreeSelectionChangeEvent>();

  statusSvojstvaEnum = StatusSvojstvaEnum;
  statusElementaPorukeEnum = StatusElementaPorukeEnum;

  hijerarhijskaStruktura: GenericTreeType[] = [];
  checklistSelection = new SelectionModel<Node>(true)
  treeControl = new FlatTreeControl<Node>(
    node => node?.level, node => node.expandable
  );

  hasChild = (_: number, node: Node) => node.expandable;

  private _transformer = (node: GenericTreeType, level: number) => {
    return {
      expandable: !!node.podredjene && node.podredjene.length > 0,
      name: node.name,
      level: level,
      parentId: node.parentId,
      nadredjenoSuzenoSvojstvoId: node.nadredjenoSuzenoSvojstvoId,
      id: node.id,
      sifra: node.sifra,
      jePodatak: node.jePodatak,
      statusNaTipuId: node.statusNaTipuId,
      statusNaPojmuId: node.statusNaPojmuId,
      statusId: node.statusId,
      jeDozvoljenVisestrukiIzbor: node.jeDozvoljenVisestrukiIzbor,
      redniBroj: node.redniBroj,
      putanja: node.putanja,
      putanjaNadredjenogSvojstva: node.putanjaNadredjenog,
      highlighted: node.highlighted
    }
  }

  treeFlattener = new MatTreeFlattener(
    this._transformer, node => node.level, node => node.expandable, node => node.podredjene
  )

  dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

  constructor() { }


  ngOnInit(): void {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes) {

      if (this.sacuvajExpandState) {
        this.sacuvajExpandovaneNodove();
      }

      if (changes.podaci) {
        this.dataSource.data = this.mapirajPodatke(changes.podaci?.currentValue);
        this.dugmiciZaAkcije = changes.dugmiciZaAkcije?.currentValue;
        this.postaviSelektovaneCheckboxove(this.sifreZaSelectCheckboxa);

        if (this.sacuvajExpandState) {
          if (this.expandAll) {
            this.treeControl.expandAll();
          }
          else if (this.expandFirstLevel) {
            this.treeControl.expand(this.treeControl.dataNodes.find(x => x.parentId == null));
          }
          else {
            this.vratiExpandState();
          }
        }
        else {
          if (this.expandAll) this.treeControl.expandAll();
          if (this.expandFirstLevel) this.treeControl.expand(this.treeControl.dataNodes.find(x => x.parentId == null));
        }
      }
      if (changes.sifreZaSelectCheckboxa) {
        this.postaviSelektovaneCheckboxove(this.sifreZaSelectCheckboxa);
      }
    }
  }

  expandedNodeIds = new Set<number>();
  expandedNodePutanje = new Set<string>();
  sacuvajExpandovaneNodove() {
    this.expandedNodeIds.clear();
    this.expandedNodePutanje.clear();

    const selected = this.treeControl.expansionModel.selected || [];
    selected.forEach((n: any) => {
      if (!n) return;
      if (this.prekoPutanje) {
        if (n.putanja) this.expandedNodePutanje.add(n.putanja);
      } else {
        if (n.id != null) this.expandedNodeIds.add(n.id);
      }
    });
  }

  vratiExpandState() {
    if (!this.treeControl.dataNodes?.length) return;

    if (this.prekoPutanje) {
      this.expandedNodePutanje.forEach(p => {
        const node = this.treeControl.dataNodes.find(n => n.putanja === p);
        if (node) this.treeControl.expand(node);
      });
    } else {
      this.expandedNodeIds.forEach(id => {
        const node = this.treeControl.dataNodes.find(n => n.id === id);
        if (node) this.treeControl.expand(node);
      });
    }
  }

  mapirajPodatke(podaci: GenericTreeType[]) {
    if (!podaci) return [];
    if (this.prekoPutanje) {
      return this.napraviHijerarhijskuStrukturuZaPutanju(podaci, null);
    }
    else {
      return this.napraviHijerarhijskuStrukturu(null, null, podaci)
    }
  }

  napraviHijerarhijskuStrukturu(id: number, suzeniTipId: number, sve: GenericTreeType[]) {
    let podredjene = sve.filter(x => x.parentId == id && ((suzeniTipId != null && suzeniTipId == x.nadredjenoSuzenoSvojstvoId) || suzeniTipId == null));
    let tree: GenericTreeType[] = [];
    for (let g of podredjene) {
      let podredjena = new GenericTreeType(
        g.id,
        g.name,
        g.sifra,
        g.parentId,
        g.suzeniTipId,
        g.nadredjenoSuzenoSvojstvoId,
        g.jePodatak,
        g.statusNaTipuId,
        g.statusNaPojmuId,
        g.statusId,
        g.jeDozvoljenVisestrukiIzbor,
        g.idZaCuvanje,
        g.isDisabled,
        g.redniBroj
      );

      podredjena.highlighted = g.highlighted;

      if (podredjena.id == id && podredjena.parentId == id) {
        tree.push(podredjena);
      }
      else {
        podredjena.podredjene = this.napraviHijerarhijskuStrukturu(g.id, g.suzeniTipId != null ? g.id : g.nadredjenoSuzenoSvojstvoId, sve);
        tree.push(podredjena);
      }
    }
    return tree;
  }

  napraviHijerarhijskuStrukturuZaPutanju(
    sve: GenericTreeType[],
    putanja: string) {
    let podredjene = sve.filter(x => x.putanjaNadredjenog == putanja);
    let tree: GenericTreeType[] = [];
    for (let g of podredjene) {
      let podredjena = new GenericTreeType(
        g.id,
        g.name,
        g.sifra,
        g.parentId,
        g.suzeniTipId,
        g.nadredjenoSuzenoSvojstvoId,
        g.jePodatak,
        g.statusNaTipuId,
        g.statusNaPojmuId,
        g.statusId,
        g.jeDozvoljenVisestrukiIzbor,
        g.idZaCuvanje,
        g.isDisabled,
        g.redniBroj
      );

      podredjena.putanja = g.putanja;
      podredjena.putanjaNadredjenog = g.putanjaNadredjenog;
      podredjena.highlighted = g.highlighted;

      podredjena.podredjene = this.napraviHijerarhijskuStrukturuZaPutanju(sve, g.putanja);
      tree.push(podredjena);
    }
    return tree;
  }

  extraBtnClick(el, btnAction) {
    this.dugmiciZaAkcijeClick.emit({
      data: el,
      btnAction: btnAction
    });
  }

  todoLeafItemSelectionToggle(node: Node) {
    if (this.singleCb) {
      this.checklistSelection.deselect(...this.checklistSelection.selected);
    }
    this.checklistSelection.toggle(node);
    this.todoLeafItemSelectionToggleClick.emit();
  }


  addDescendants(node: Node, descendants: Node[]) {
    var nodeDescendands = this.treeControl.getDescendants(node);
    if (nodeDescendands.length > 0) {
      descendants = [...nodeDescendands];

      descendants.forEach(descendant => {
        this.addDescendants(descendant, descendants);
      });
    }
    return descendants;
  }

  getAllDescendants(root: any): any[] {
    const descendants = this.treeControl.getDescendants(root);
    let allDescendants: any[] = [...descendants];

    descendants.forEach(child => {
      allDescendants = allDescendants.concat(this.getAllDescendants(child));
    });

    return allDescendants;
  }

  isAnyElementSelected(allDescendants: any[], selectedList: any[]): boolean {
    return allDescendants.some(descendant =>
      selectedList.includes(descendant)
    );
  }

  isDisabled(node: Node) {
    if (node.parentId == null) return false;

    var parentNode = this.podaci.find(x => x.id == node.parentId);
    var rootNode = this.podaci.find(x => x.id == node.parentId);

    while (rootNode.parentId != null) {
      rootNode = this.podaci.find(x => x.id == parentNode.parentId);
    }

    if (!rootNode.jeDozvoljenVisestrukiIzbor) {
      var root = this.treeControl.dataNodes.find(x => x.id == rootNode.id);
      var descendants = this.getAllDescendants(root).filter(x => x.id != node.id);

      return this.isAnyElementSelected(descendants, this.checklistSelection.selected);
    }

    return false;
  }

  dajSelektovaneCvorove() {
    return this.checklistSelection.selected;
  }

  postaviSelektovaneCheckboxove(sifre) {
    let selektovaniCvorovi = this.treeControl.dataNodes.filter(x => sifre.includes(x.sifra));
    selektovaniCvorovi.forEach(el => {
      let parentNode = this.treeControl.dataNodes.find(x => x.id == el.parentId && x.id != el.id);
      this.treeControl.expand(parentNode);
      this.checklistSelection.select(el);
    })
  }
}
